import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';

import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDropdown } from '@/hooks/useDropdown';
import { Translation, useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa } from '@/lib/exportCsv';
import { splitLocalizedName } from '@/lib/splitHostName';
import { additionalValue, Pagination } from '@/react-query/context/ApiClientContextController';
import { HostListItem } from '@/services/hostManagement/hostManagement.types';
import useHostMutation from '../hooks/useHostMutation';
import { getColumns } from './Column';

export function HostManagementTable({
  data,
  isLoading,
  pagination,
  commonClubId,
}: {
  data: HostListItem[];
  isLoading: boolean;
  pagination: Pagination;
  commonClubId: additionalValue;
}) {
  const { mutate } = useHostMutation();
  const { openConfirmation } = useConfirmationStore();
  const { locale, t } = useDynamicLocale();
  const { data: options } = useDropdown(
    'clubName',
    commonClubId?.commonClubId,
    undefined,
    undefined,
    Boolean(commonClubId?.commonClubId),
  );
  const { japanese, english } = splitLocalizedName(options?.[0]?.label);
  const clubName = locale === 'en' ? english : japanese;
  const columns = React.useMemo(() => getColumns(mutate, openConfirmation, t), [mutate, openConfirmation, t]);

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );

  const finalData = data?.map((item: HostListItem, index: number) => {
    return {
      SN: index + 1,
      Position: item.position,
      'Staff Name': item.hostNameEng,
      Status: item.status === 'A' ? 'Active' : 'Blocked',
      'Create Date': item.createdDate,
      'Update Date': item.updatedDate,
    };
  });

  function handleExport() {
    exportCsvSingleFilePapa(finalData, {
      format: 'YYYY/MM/DD HH:mm:ss',
    });
  }

  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'clubId', desc: true }],
    },
    meta: {
      title: clubName ? (
        <Translation id="storeManagement.hostManagement.page.table.title" values={{ clubName: clubName }} />
      ) : (
        ''
      ),
      actionKey: 'exportCsv',
      action: handleExport,
      loading: isLoading,
      totalPage: pagination.totalItems,
    },
    getRowId: (originalRow) => String(originalRow.id),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar />
      </DataTable>
    </>
  );
}
